import React, { useState } from 'react';
import { dataLoaderService, LoadProgress } from '../../services/dataLoaderService';
import { CURRENT_SEASON, AVAILABLE_SEASONS } from '../../config/season';

const DataLoader: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<LoadProgress | null>(null);
  const [season, setSeason] = useState(CURRENT_SEASON);
  const [selectedWeek, setSelectedWeek] = useState(1);

  const handleLoadSeasonData = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setProgress(null);

    try {
      const finalProgress = await dataLoaderService.loadAllSeasonGames(
        season,
        (progressUpdate) => {
          setProgress(progressUpdate);
        }
      );
      
      setProgress(finalProgress);
    } catch (error) {
      console.error('Error loading season data:', error);
      setProgress({
        currentWeek: 0,
        totalWeeks: 18,
        gamesLoaded: 0,
        isComplete: true,
        errors: [error instanceof Error ? error.message : 'Unknown error occurred']
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadLiveOdds = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      const result = await dataLoaderService.loadLiveOdds(season);
      
      setProgress({
        currentWeek: 18,
        totalWeeks: 18,
        gamesLoaded: result.success,
        isComplete: true,
        errors: result.errors > 0 ? [`${result.errors} games failed to update`] : []
      });
    } catch (error) {
      console.error('Error loading live odds:', error);
      setProgress({
        currentWeek: 0,
        totalWeeks: 18,
        gamesLoaded: 0,
        isComplete: true,
        errors: [error instanceof Error ? error.message : 'Unknown error occurred']
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadSpecificWeek = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setProgress(null);

    try {
      const result = await dataLoaderService.refreshWeekGames(season, selectedWeek);
      
      setProgress({
        currentWeek: selectedWeek,
        totalWeeks: 18,
        gamesLoaded: result.gamesUpdated,
        isComplete: true,
        errors: result.error ? [result.error] : []
      });
    } catch (error) {
      console.error('Error loading week data:', error);
      setProgress({
        currentWeek: selectedWeek,
        totalWeeks: 18,
        gamesLoaded: 0,
        isComplete: true,
        errors: [error instanceof Error ? error.message : 'Unknown error occurred']
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="data-loader-container" style={{ padding: '20px', border: '1px solid #ddd', margin: '20px', borderRadius: '8px' }}>
      <h2>NFL Season Data Loader</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="season-select" style={{ marginRight: '10px' }}>
          Season:
        </label>
        <select
          id="season-select"
          value={season}
          onChange={(e) => setSeason(Number(e.target.value))}
          disabled={isLoading}
          style={{ padding: '5px', marginRight: '15px' }}
        >
          {AVAILABLE_SEASONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        
        <button
          onClick={handleLoadSeasonData}
          disabled={isLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: isLoading ? '#ccc' : '#007cba',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          {isLoading ? 'Loading...' : `Load All ${season} Games`}
        </button>
        
        <button
          onClick={handleLoadLiveOdds}
          disabled={isLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: isLoading ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? 'Loading...' : 'Load Live Odds'}
        </button>
      </div>

      <div style={{ marginBottom: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
        <label htmlFor="week-select" style={{ marginRight: '10px' }}>
          Load Games for Specific Week:
        </label>
        <select
          id="week-select"
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(Number(e.target.value))}
          disabled={isLoading}
          style={{ padding: '5px', marginRight: '15px' }}
        >
          {Array.from({length: 18}, (_, i) => i + 1).map(week => (
            <option key={week} value={week}>Week {week}</option>
          ))}
        </select>
        
        <button
          onClick={handleLoadSpecificWeek}
          disabled={isLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: isLoading ? '#ccc' : '#ffc107',
            color: '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? 'Loading...' : `Load Week ${selectedWeek} Games`}
        </button>
      </div>

      {progress && (
        <div className="progress-info" style={{ marginTop: '20px' }}>
          <h3>Loading Progress</h3>
          
          {!progress.isComplete && (
            <div style={{ marginBottom: '10px' }}>
              <div>Week: {progress.currentWeek}/{progress.totalWeeks}</div>
              <div style={{ 
                width: '100%', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '4px',
                marginTop: '5px'
              }}>
                <div
                  style={{
                    width: `${(progress.currentWeek / progress.totalWeeks) * 100}%`,
                    height: '20px',
                    backgroundColor: '#007cba',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ marginTop: '10px' }}>
            <strong>Games Loaded: {progress.gamesLoaded}</strong>
          </div>

          {progress.errors.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <h4 style={{ color: 'red' }}>Errors:</h4>
              <ul style={{ color: 'red' }}>
                {progress.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {progress.isComplete && progress.errors.length === 0 && (
            <div style={{ color: 'green', marginTop: '10px', fontWeight: 'bold' }}>
              ✅ Season data loaded successfully!
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
        <p><strong>Load All Games:</strong></p>
        <ul>
          <li>Fetches all regular season games (weeks 1-18) from ESPN</li>
          <li>Saves game schedules and team information to your database</li>
          <li>Clears existing data for the selected season first</li>
          <li>Takes about 30-60 seconds to complete</li>
        </ul>
        
        <p><strong>Load Live Odds:</strong></p>
        <ul>
          <li>Fetches current betting lines and spreads from ESPN's dedicated odds API</li>
          <li>Updates existing games with live spread data</li>
          <li>Run this after loading games to get current betting lines</li>
          <li>Can be run multiple times to refresh odds</li>
        </ul>

        <p><strong>Load Games for Specific Week:</strong></p>
        <ul>
          <li>Fetches games for a single week from ESPN</li>
          <li>Updates only that week's games in the database</li>
          <li>Useful for refreshing or updating a specific week after changes</li>
          <li>Faster than loading the entire season</li>
        </ul>
      </div>
    </div>
  );
};

export default DataLoader;